/**
 * Модель для работы с реестром СИЗ
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';
import { v4 as uuidv4 } from 'uuid';

import { IProtection, IProtectionType, IProtectionTechnology, IShortUser, IId } from './types';

import ProtectionsService, { IUpdateOrCreateBody } from '../services/ProtectionsService';
import UsersService from '../services/UsersService';

interface IData {
  types: IProtectionType[];
  technology: IProtectionTechnology[];
  users: IShortUser[];
}

interface IList {
  data: IProtection[] | null;
  pagesCount: number;
}

export interface IForm {
  uid: IId;
  isValidate: boolean;
}

interface IForms {
  list: IForm[];
  isSubmit: boolean;
}

interface IState {
  data: IData;
  list: IList;
  item: IUpdateOrCreateBody | null;
  forms: IForms;
}

const initialState: IState = {
  data: {
    types: [],
    technology: [],
    users: []
  },
  list: {
    data: null,
    pagesCount: 0
  },
  item: null,
  forms: {
    list: [
      {
        uid: uuidv4(),
        isValidate: false
      }
    ],
    isSubmit: false
  }
};

export const equipmentProtections = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setData(state: IState, payload: IData): IState {
      return { ...state, data: payload };
    },
    setList(state: IState, payload: IList): IState {
      return { ...state, list: payload };
    },
    setItem(state: IState, payload: IUpdateOrCreateBody): IState {
      return { ...state, item: payload };
    },
    addForm(state: IState): IState {
      return {
        ...state,
        forms: {
          ...state.forms,
          list: [...state.forms.list, { uid: uuidv4(), isValidate: false }]
        }
      };
    },
    deleteForm(state: IState, uid: IId): IState {
      return {
        ...state,
        forms: {
          ...state.forms,
          list: state.forms.list.filter(item => item.uid !== uid)
        }
      };
    },
    editForm(state: IState, payload: IForm) {
      const { uid } = payload;

      return {
        ...state,
        forms: {
          ...state.forms,
          list: state.forms.list.map(item => (item.uid === uid ? payload : item))
        }
      };
    },
    setSubmitForms(state: IState, payload: boolean): IState {
      return { ...state, forms: { ...state.forms, isSubmit: payload } };
    },
    reset(): IState {
      return { ...initialState };
    }
  },
  effects: () => ({
    async getData() {
      try {
        const [
          {
            data: { data: types }
          },
          { data: technology },
          { data: users }
        ] = await Promise.all([
          ProtectionsService.getTypeAll({ classType: 'PROT', pageSize: 100 }),
          ProtectionsService.getTechnologyAll(),
          UsersService.getAllEmployeesShort()
        ]);

        this.setData({ types, technology, users });
      } catch (error) {
        return error;
      }
    },
    async getList(params: any) {
      try {
        const { data } = await ProtectionsService.getAll(params);

        this.setList(data);
      } catch (error) {
        return error;
      }
    },
    async getItem(id: IId) {
      try {
        const { data } = await ProtectionsService.get(id);

        this.setItem(data);
      } catch (error) {
        return error;
      }
    },
    async updateOrCreate(body: IUpdateOrCreateBody) {
      try {
        await ProtectionsService.updateOrCreate(body);
      } catch (error) {
        return error;
      }
    },
    async unbind(id: IId) {
      try {
        await ProtectionsService.unbind(id);
      } catch (error) {
        return error;
      }
    },
    async remove(id: IId) {
      try {
        await ProtectionsService.delete(id);
      } catch (error) {
        return error;
      }
    }
  })
});
